import React, { useState, useEffect, useCallback } from 'react';
import { Send, X, AlertTriangle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { previewTransfer, transferToSap } from './materialApi';

/**
 * TransferDialog — zeigt vor der Übertragung das erzeugte BAPI_MATERIAL_SAVEDATA-Payload
 * (zur fachlichen Abstimmung), dann „An SAP übertragen (Stub)".
 *
 * Props: user, materialId, materialLabel, onDone(), onClose(), showSuccess, showError
 */
const TransferDialog = ({ user, materialId, materialLabel, onDone, onClose, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await previewTransfer(user, materialId);
      setPayload(res.payload || null);
    } catch { showError?.(t('material.trfPreviewError')); }
    finally { setLoading(false); }
  }, [user, materialId, showError, t]);
  useEffect(() => { load(); }, [load]);

  const doTransfer = async () => {
    setBusy(true);
    try {
      const res = await transferToSap(user, materialId);
      showSuccess?.(res?.message || t('material.trfDone'));
      onDone?.();
    } catch (e) { showError?.(e.message || t('material.trfError')); }
    finally { setBusy(false); }
  };

  const panel = isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-slate-200';
  const sectionTitle = isDarkMode ? 'text-white/80' : 'text-slate-700';
  const kvBox = isDarkMode ? 'bg-white/5' : 'bg-slate-50';

  // Daten-Strukturen (X-Strukturen technisch ausgeblendet)
  const DATA_STRUCTS = ['HEADDATA', 'CLIENTDATA', 'PLANTDATA', 'VALUATIONDATA', 'SALESDATA'];

  const renderKV = (obj) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <span className={`font-mono text-[10px] ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>{k}</span>
          <span className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>{String(v)}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onMouseDown={onClose}>
      <div className={`w-full max-w-2xl rounded-2xl shadow-2xl ${panel} flex flex-col max-h-[85vh]`} onMouseDown={(e) => e.stopPropagation()}>
        {/* Kopf */}
        <div className={`flex items-start gap-3 p-5 border-b ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDarkMode ? 'bg-blue-500/15' : 'bg-blue-50'}`}>
            <Send className="w-5 h-5 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('material.trfTitle')}</h3>
            <p className={`text-xs mt-0.5 truncate ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              {materialLabel} · BAPI_MATERIAL_SAVEDATA
            </p>
          </div>
          <button type="button" onClick={onClose} className={`p-1 rounded-lg ${isDarkMode ? 'text-white/50 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Stub-Hinweis */}
        <div className={`mx-5 mt-4 rounded-xl px-3 py-2 text-xs flex items-start gap-2 ${isDarkMode ? 'bg-amber-500/10 text-amber-200' : 'bg-amber-50 text-amber-700'}`}>
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{t('material.trfStubNote')}</span>
        </div>

        {/* Payload */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading ? (
            <div className={`text-center text-sm py-8 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{t('common.loading')}</div>
          ) : !payload ? (
            <div className={`text-center text-sm py-8 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>—</div>
          ) : (
            <>
              {DATA_STRUCTS.filter((s) => payload[s]).map((s) => (
                <div key={s}>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>{s}</div>
                  <div className={`rounded-xl p-3 ${kvBox}`}>{renderKV(payload[s])}</div>
                </div>
              ))}
              {Array.isArray(payload.MATERIALDESCRIPTION) && (
                <div>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>MATERIALDESCRIPTION</div>
                  <div className={`rounded-xl p-3 space-y-1 ${kvBox}`}>
                    {payload.MATERIALDESCRIPTION.map((d, i) => (
                      <div key={i} className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>
                        <span className="font-mono text-[10px] mr-2">{d.LANGU}</span>{d.MATL_DESC}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {Array.isArray(payload.MATERIALLONGTEXT) && (
                <div>
                  <div className={`text-xs font-semibold mb-1.5 ${sectionTitle}`}>MATERIALLONGTEXT</div>
                  <div className={`rounded-xl p-3 space-y-1 ${kvBox}`}>
                    {payload.MATERIALLONGTEXT.map((l, i) => (
                      <div key={i} className={`text-sm ${isDarkMode ? 'text-white/85' : 'text-slate-700'}`}>
                        <span className="font-mono text-[10px] mr-2">{l.TEXT_ID}/{l.LANGU}</span>{l.TEXT_LINE}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {payload._unmapped && Object.keys(payload._unmapped).length > 0 && (
                <div>
                  <div className="text-xs font-semibold mb-1.5 text-amber-500">{t('material.trfUnmapped')}</div>
                  <div className={`rounded-xl p-3 border border-amber-400/30 ${isDarkMode ? 'bg-amber-500/5' : 'bg-amber-50/50'}`}>{renderKV(payload._unmapped)}</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Aktionen */}
        <div className={`flex items-center justify-end gap-3 p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-slate-100'}`}>
          <button type="button" onClick={onClose}
            className={`px-4 py-2 rounded-xl text-sm font-medium ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}>
            {t('material.dupCancel')}
          </button>
          <button type="button" onClick={doTransfer} disabled={busy || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
            <Send className="w-4 h-4" /> {t('material.trfConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferDialog;
